<template>
  <page-outline :title="$t('mandate.sdd_blacklist.title')">
    <template slot="pageContent">
      <b-container fluid>
        <div class="requestsList">

          <!-- ACTION BAR -->
          <b-row class="mt-3">
            <b-col cols="6" class="d-flex justify-content-start">
              <b-button class="btnFilter_2" @click="onRefresh">
                <i class="fa-light fa-rotate-right"></i>
                {{ $t('common.rfrsh') }}
              </b-button>
            </b-col>

            <b-col cols="6" class="d-flex justify-content-end">
              <b-button class="btnFilter_2" @click="openCreatePopup">
                <i class="fa-light fa-plus"></i>
                {{ $t('mandate.sdd_blacklist.generation_button') }}
              </b-button>
            </b-col>
          </b-row>

          <hr class="mx-auto" />

          <!-- EXISTING BLACKLIST -->
          <b-row>
            <b-col cols="12">
              <h5 class="mb-3">
                {{ $t('mandate.sdd_blacklist.existing_blacklist') }}
              </h5>
            </b-col>

            <b-col cols="12" class="mx-auto mb-4">
              <div>
                <spinner v-if="activateSpinner"></spinner>
                  <b-table
                    v-else-if="tableItems.length > 0"
                    hover
                    striped
                    responsive
                    no-local-sorting
                    class="table table-striped"
                    style="margin-bottom:0"
                    :items="paginatedTableItems"
                    :fields="fields"
                    :sort-by.sync="sortBy"
                    :sort-desc.sync="sortDesc"
                    :empty-text="$t('common.no_result')"
                    :key="refreshKey"
                    @sort-changed="onSortChanged"
                  >
                  <template #cell(actions)="{ item }">
                    <div class="d-flex justify-content-center align-items-center">

                      <b-button
                        variant="link"
                        class="action-icon p-1 mr-2"
                        :class="{ 'action-icon-disabled': String(item.editable) !== '1' }"
                        :disabled="String(item.editable) !== '1'"
                        :title="$t('mandate.sdd_blacklist.edit')"
                        @click.stop="openEditPopup(item)"
                      >
                        <i class="fa-light fa-pencil"></i>
                      </b-button>

                      <b-button
                        variant="link"
                        class="action-icon action-icon-delete p-1"
                        :class="{ 'action-icon-disabled': String(item.editable) !== '1' }"
                        :disabled="String(item.editable) !== '1'"
                        :title="$t('mandate.sdd_blacklist.delete')"
                        @click.stop="deleteBlacklist(item)"
                      >
                        <i class="fa-light fa-xmark"></i>
                      </b-button>

                    </div>
                  </template>
                </b-table>
                <b-row v-if="!activateSpinner" class="mt-1">
                  <b-col cols="12">
                    <cm-pagination
                      :maxResultsByPage="paginationObject.maxResults"
                      :firstIndex="paginationObject.firstIndex"
                      :nbResultsTotal="paginationObject.nbResultsTotal"
                      :refreshEvent="paginationObject.refresh"
                    />
                  </b-col>
                </b-row>

                <div v-else class="d-flex justify-content-center">
                  <span class="noResult">
                    {{ $t('common.no_result') }}
                  </span>
                </div>
              </div>
            </b-col>
          </b-row>
        </div>

        <blacklist-pop-up
          v-if="showBlacklistPopup"
          :form="popupForm"
          :type="popupType"
          :is-edit="isEditMode"
          @close="closePopup"
          @savepopup="savePopup"
        />
      </b-container>
    </template>
  </page-outline>
</template>

<script>
import PageOutline from '@/components/common/PageOutline.vue'
import Spinner from '@/components/common/spinner.vue'
import BlacklistPopUp from '@/popups/blacklist/BlacklistPopUp.vue'
import { errorMessageAlert } from '@/helpers/alert'
import Pagination from '@/components/common/pagination.vue'

export default {
  name: 'SDDBlacklist',

  components: {
    'page-outline': PageOutline,
    spinner: Spinner,
    'blacklist-pop-up': BlacklistPopUp,
    'cm-pagination': Pagination
  },

  data () {
    return {
      activateSpinner: false,
      saving: false,
      refreshKey: false,

      blacklistItems: [],

      sortBy: '',
      sortDesc: false,

      paginationObject: {
        maxResults: 15,
        firstIndex: 1,
        nbResultsTotal: 0,
        refresh: 'refreshSDDBlacklist'
      },

      showBlacklistPopup: false,
      isEditMode: false,
      editingBlacklistId: null,
      popupType: '',
      popupForm: {
        country_code: '',
        bank_code: '',
        bic: '',
        full_iban: ''
      },

      fields: [
        {
          key: 'category',
          label: this.$t('mandate.sdd_blacklist.category'),
          sortable: true,
          class: 'text-center'
        },
        {
          key: 'value',
          label: this.$t('mandate.sdd_blacklist.value'),
          sortable: true,
          class: 'text-center'
        },
        {
          key: 'name_account',
          label: this.$t('administration.company'),
          sortable: true,
          class: 'text-center'
        },
        {
          key: 'actions',
          label: this.$t('mandate.sdd_blacklist.actions'),
          class: 'text-center'
        }
      ]
    }
  },

  computed: {
    sortedTableItems () {
      const items = [...this.tableItems]

      if (!this.sortBy) {
        return items
      }

      const key = this.sortBy

      items.sort((a, b) => {
        const valueA = (a[key] || '').toString().toLowerCase()
        const valueB = (b[key] || '').toString().toLowerCase()

        const result = valueA.localeCompare(valueB)

        return this.sortDesc ? -result : result
      })

      return items
    },
    appLanguage () {
      return this.$i18n.locale
    },
    paginatedTableItems () {
      const start = this.paginationObject.firstIndex - 1
      const end = start + this.paginationObject.maxResults

      return this.sortedTableItems.slice(start, end)
    },
    tableItems () {
      return this.blacklistItems.map(item => {
        const categories = []
        const values = []

        if (item.country_code) {
          categories.push(this.$t('mandate.sdd_blacklist.country_code'))
          values.push(item.country_code)
        }

        if (item.bank_code) {
          categories.push(this.$t('mandate.sdd_blacklist.bank_code'))
          values.push(item.bank_code)
        }

        if (item.bic) {
          categories.push(this.$t('mandate.sdd_blacklist.bic'))
          values.push(item.bic)
        }

        if (item.full_iban) {
          categories.push(this.$t('mandate.sdd_blacklist.full_iban'))
          values.push(item.full_iban)
        }

        return {
          ...item,
          category: categories.join(' - '),
          value: values.join(' - ')
        }
      })
    }
  },
  watch:  {
    appLanguage(newValue, oldValue) {
      this.fields = [
        {
          key: 'category',
          label: this.$t('mandate.sdd_blacklist.category'),
          sortable: true,
          class: 'text-center'
        },
        {
          key: 'value',
          label: this.$t('mandate.sdd_blacklist.value'),
          sortable: true,
          class: 'text-center'
        },
        {
          key: 'name_account',
          label: this.$t('administration.company'),
          sortable: true,
          class: 'text-center'
        },
        {
          key: 'actions',
          label: this.$t('mandate.sdd_blacklist.actions'),
          class: 'text-center'
        }
      ]
    }
  },

  created () {
    this.$bus.$on('refreshSDDBlacklist', this.changeBlacklistPage)
    this.getBlacklist()
  },
  destroyed () {
    this.$bus.$off('refreshSDDBlacklist', this.changeBlacklistPage)
  },

  methods: {
    onSortChanged (ctx) {
      this.sortBy = ctx.sortBy
      this.sortDesc = ctx.sortDesc

      // Revenir à la page 1 après un nouveau tri
      this.paginationObject.firstIndex = 1
    },
    changeBlacklistPage (firstIndex) {
      this.paginationObject.firstIndex = firstIndex
    },
    getBlacklist () {
      const that = this
      this.activateSpinner = true

      this.$engine.SDD_Blacklist.getSDD_Blacklist({})
        .then((resp) => {
          const data = resp && resp.data ? resp.data : {}

          // Nouveau format backend :
          // {
          //   "Blaklist": [...],
          //   "nbTotalResults": 8
          // }
          that.blacklistItems = Array.isArray(data.Blaklist)
            ? data.Blaklist
            : []

            that.paginationObject.nbResultsTotal =
              Number(data.nbTotalResults) || 0

          that.activateSpinner = false

          if (that.$f7) {
            that.$f7.pullToRefreshDone()
          }
        })
        .catch((error) => {
          that.activateSpinner = false
          errorMessageAlert(error, that)
        })
    },

    onRefresh () {
      this.paginationObject.firstIndex = 1
      this.refreshKey = !this.refreshKey
      this.getBlacklist()
    },

    resetPopupForm () {
      this.popupForm = {
        country_code: '',
        bank_code: '',
        bic: '',
        full_iban: ''
      }
      this.popupType = ''
      this.editingBlacklistId = null
      this.isEditMode = false
    },

    openCreatePopup () {
      this.resetPopupForm()
      this.showBlacklistPopup = true
    },

    openEditPopup (item) {
      this.isEditMode = true
      this.editingBlacklistId = item.id_sdd_blacklist

      this.popupType = item.full_iban ? 'individual' : 'group'

      this.popupForm = {
        country_code: item.country_code || '',
        bank_code: item.bank_code || '',
        bic: item.bic || '',
        full_iban: item.full_iban || ''
      }

      this.showBlacklistPopup = true
    },

    closePopup () {
      this.showBlacklistPopup = false
      this.resetPopupForm()
    },

    savePopup (payload) {
      if (this.saving) return

      this.saving = true

      const params = {
        country_code: payload.form.country_code || null,
        bank_code: payload.form.bank_code || null,
        bic: payload.form.bic || null,
        full_iban: payload.form.full_iban || null
      }

      let request

      if (this.isEditMode) {
        request = this.$engine.SDD_Blacklist.editSDD_Blacklist({
          id_sdd_blacklist: this.editingBlacklistId,
          ...params
        })
      } else {
        request = this.$engine.SDD_Blacklist.addSDD_Blacklist(params)
      }

      request
        .then(() => {
          this.$f7.alert(
            this.$t(
              this.isEditMode
                ? 'mandate.sdd_blacklist.edit_success'
                : 'mandate.sdd_blacklist.save_success'
            ),
            this.$t('mandate.sdd_blacklist.title')
          )

          this.showBlacklistPopup = false
          this.saving = false
          this.resetPopupForm()
          this.onRefresh()
        })
        .catch((error) => {
          this.saving = false
          errorMessageAlert(error, this)
        })
    },

    deleteBlacklist (item) {
      this.$f7.confirm(
        this.$t('mandate.sdd_blacklist.delete_confirmation'),
        this.$t('mandate.sdd_blacklist.title'),
        () => {
          this.$engine.SDD_Blacklist.deleteSDD_Blacklist({
            id_sdd_blacklist: item.id_sdd_blacklist
          })
            .then(() => {
              this.$f7.alert(
                this.$t('mandate.sdd_blacklist.delete_success'),
                this.$t('mandate.sdd_blacklist.title')
              )

              this.onRefresh()
            })
            .catch((error) => {
              errorMessageAlert(error, this)
            })
        }
      )
    }
  }
}
</script>

<style scoped>
h5 {
  font-weight: 600;
}

.action-icon {
  min-width: auto;
  color: inherit;
  border: 0;
  box-shadow: none;
}

.action-icon:hover {
  text-decoration: none;
}

.action-icon-delete {
  color: #dc3545;
}

.action-icon-disabled,
.action-icon-disabled:hover,
.action-icon-disabled:focus {
  color: #b8b8b8 !important;
  opacity: 0.5;
  cursor: not-allowed !important;
  pointer-events: none;
}
</style>
